import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { PixelAvatar } from '@/components/PixelAvatar';
import { toast } from 'sonner';
import { Users, UserPlus, Copy, Check, Mail, Hash, ArrowLeft, Flame, Clock, Gift, X, UserMinus, RotateCcw, Swords, Trophy, Target } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

const FriendsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isSpanish } = useLanguage();
  
  // State
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [myFriendCode, setMyFriendCode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Custom friend code setup
  const [showSetCode, setShowSetCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [isSettingCode, setIsSettingCode] = useState(false);
  
  // Add friend modal
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [addMethod, setAddMethod] = useState('code'); // 'code' or 'email'
  
  // Challenge state
  const [challenges, setChallenges] = useState([]);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [selectedFriendForChallenge, setSelectedFriendForChallenge] = useState(null);
  const [challengeDuration, setChallengeDuration] = useState('7');
  const [challengeType, setChallengeType] = useState('overall');
  const [challengeMessage, setChallengeMessage] = useState('');
  const [isCreatingChallenge, setIsCreatingChallenge] = useState(false);
  const [friendCode, setFriendCode] = useState('');
  const [friendEmail, setFriendEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  
  // Remove friend dialog
  const [removeFriend, setRemoveFriend] = useState(null);

  // Fetch friends and requests
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch in parallel
      const [friendsRes, requestsRes, codeRes, challengesRes] = await Promise.all([
        apiFetch('/api/friends/list'),
        apiFetch('/api/friends/requests'),
        apiFetch('/api/friends/my-code'),
        apiFetch('/api/friends/challenges')
      ]);
      
      if (friendsRes.data) setFriends(friendsRes.data);
      if (requestsRes.data) setFriendRequests(requestsRes.data);
      if (codeRes.data) setMyFriendCode(codeRes.data.friend_code);
      if (challengesRes.data) setChallenges(challengesRes.data.challenges || []);
    } catch (error) {
      console.error('Error fetching friends data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Copy friend code
  const handleCopyCode = () => {
    if (!myFriendCode) return;
    navigator.clipboard.writeText(myFriendCode);
    setCopied(true);
    toast.success(isSpanish ? 'Código copiado' : 'Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Generate random code suggestion
  const handleGenerateRandom = async () => {
    try {
      const { data, error } = await apiFetch('/api/friends/generate-random-code', {
        method: 'POST'
      });
      
      if (!error && data) {
        setCustomCode(data.suggested_code);
      }
    } catch (error) {
      console.error('Error generating code:', error);
    }
  };

  // Set custom friend code
  const handleSetCode = async () => {
    if (!customCode || customCode.length < 4) {
      toast.error(isSpanish ? 'El código debe tener al menos 4 caracteres' : 'Code must be at least 4 characters');
      return;
    }
    
    setIsSettingCode(true);
    
    try {
      const { data, error } = await apiFetch('/api/friends/set-code', {
        method: 'POST',
        body: { friend_code: customCode }
      });
      
      if (!error && data) {
        toast.success(isSpanish ? 'Código establecido correctamente' : 'Friend code set successfully!');
        setMyFriendCode(data.friend_code);
        setShowSetCode(false);
        setCustomCode('');
      } else {
        toast.error(error || (isSpanish ? 'Error al establecer el código' : 'Failed to set code'));
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    } finally {
      setIsSettingCode(false);
    }
  };

  // Add friend
  const handleAddFriend = async () => {
    setIsAdding(true);
    
    try {
      const endpoint = addMethod === 'code' ? '/api/friends/add-by-code' : '/api/friends/add-by-email';
      const body = addMethod === 'code' ? { friend_code: friendCode } : { email: friendEmail };
      
      const { data, error } = await apiFetch(endpoint, {
        method: 'POST',
        body
      });
      
      if (!error) {
        toast.success(data?.message || (isSpanish ? 'Solicitud enviada' : 'Friend request sent'));
        setShowAddFriend(false);
        setFriendCode('');
        setFriendEmail('');
        fetchData();
      } else {
        toast.error(error || (isSpanish ? 'Error al agregar amigo' : 'Failed to add friend'));
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    } finally {
      setIsAdding(false);
    }
  };

  // Accept friend request
  const handleAcceptRequest = async (friendshipId) => {
    try {
      const { error } = await apiFetch(`/api/friends/accept/${friendshipId}`, {
        method: 'POST'
      });
      
      if (!error) {
        toast.success(isSpanish ? 'Solicitud aceptada' : 'Friend request accepted');
        fetchData();
      } else {
        toast.error(error || 'Failed to accept request');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Decline friend request
  const handleDeclineRequest = async (friendshipId) => {
    try {
      const { error } = await apiFetch(`/api/friends/decline/${friendshipId}`, {
        method: 'POST'
      });
      
      if (!error) {
        toast.success(isSpanish ? 'Solicitud rechazada' : 'Friend request declined');
        fetchData();
      } else {
        toast.error(error || 'Failed to decline request');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Remove friend
  const handleRemoveFriend = async () => {
    if (!removeFriend) return;
    
    try {
      const { error } = await apiFetch(`/api/friends/${removeFriend.friendship_id}`, {
        method: 'DELETE'
      });
      
      if (!error) {
        toast.success(isSpanish ? 'Amigo eliminado' : 'Friend removed');
        setRemoveFriend(null);
        fetchData();
      } else {
        toast.error(error || 'Failed to remove friend');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Challenge functions
  const openChallengeModal = (friend) => {
    setSelectedFriendForChallenge(friend);
    setChallengeDuration('7');
    setChallengeType('overall');
    setChallengeMessage('');
    setShowChallengeModal(true);
  };

  const handleCreateChallenge = async () => {
    if (!selectedFriendForChallenge) return;
    
    setIsCreatingChallenge(true);
    try {
      const { data, error } = await apiFetch('/api/friends/challenges/create', {
        method: 'POST',
        body: {
          friend_user_id: selectedFriendForChallenge.friend_user_id,
          duration_days: parseInt(challengeDuration),
          challenge_type: challengeType,
          message: challengeMessage || null
        }
      });
      
      if (!error) {
        toast.success(data?.message || (isSpanish ? '¡Desafío enviado!' : 'Challenge sent!'));
        setShowChallengeModal(false);
        fetchData();
      } else {
        toast.error(error || (isSpanish ? 'Error al crear desafío' : 'Failed to create challenge'));
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    } finally {
      setIsCreatingChallenge(false);
    }
  };

  const handleAcceptChallenge = async (challengeId) => {
    try {
      const { error } = await apiFetch(`/api/friends/challenges/${challengeId}/accept`, {
        method: 'POST'
      });
      
      if (!error) {
        toast.success(isSpanish ? '¡Desafío aceptado!' : 'Challenge accepted!');
        fetchData();
      } else {
        toast.error(error || 'Failed to accept');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDeclineChallenge = async (challengeId) => {
    try {
      const { error } = await apiFetch(`/api/friends/challenges/${challengeId}/decline`, {
        method: 'POST'
      });
      
      if (!error) {
        toast.success(isSpanish ? 'Desafío rechazado' : 'Challenge declined');
        fetchData();
      } else {
        toast.error(error || 'Failed to decline');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Get pending challenges for current user
  const pendingChallenges = challenges.filter(c => c.status === 'pending' && !c.is_challenger);
  const activeChallenges = challenges.filter(c => c.status === 'active');
  const completedChallenges = challenges.filter(c => c.status === 'completed');

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <Header />
        <div className="container py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <Header />
      
      <div className="container py-4 sm:py-6 max-w-3xl px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-3 -ml-2"
          size="sm"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {isSpanish ? 'Atrás' : 'Back'}
        </Button>
        
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-6 w-6 text-[#00E5CC]" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">{isSpanish ? 'Amigos' : 'Friends'}</h1>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Conecta y compite con amigos' : 'Connect and compete with friends'}
              </p>
            </div>
          </div>
          
          <Dialog open={showAddFriend} onOpenChange={setShowAddFriend}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-friend-btn">
                <UserPlus className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{isSpanish ? 'Agregar Amigo' : 'Add Friend'}</span>
                <span className="sm:hidden">{isSpanish ? 'Agregar' : 'Add'}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{isSpanish ? 'Agregar Amigo' : 'Add Friend'}</DialogTitle>
                <DialogDescription>
                  {isSpanish 
                    ? 'Envía una solicitud de amistad por código o email'
                    : 'Send a friend request by code or email'}
                </DialogDescription>
              </DialogHeader>
              
              <Tabs value={addMethod} onValueChange={setAddMethod} className="mt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="code">
                    <Hash className="h-4 w-4 mr-2" />
                    {isSpanish ? 'Código' : 'Code'}
                  </TabsTrigger>
                  <TabsTrigger value="email">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="code" className="mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="friend-code">{isSpanish ? 'Código de Amigo' : 'Friend Code'}</Label>
                    <Input
                      id="friend-code"
                      placeholder="ABC12345"
                      value={friendCode}
                      onChange={(e) => setFriendCode(e.target.value.toUpperCase())}
                      maxLength={8}
                      className="uppercase"
                      data-testid="friend-code-input"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="email" className="mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="friend-email">Email</Label>
                    <Input
                      id="friend-email"
                      type="email"
                      placeholder="friend@example.com"
                      value={friendEmail}
                      onChange={(e) => setFriendEmail(e.target.value)}
                      data-testid="friend-email-input"
                    />
                  </div>
                </TabsContent>
              </Tabs>
              
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowAddFriend(false)}>
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={handleAddFriend} 
                  disabled={isAdding || (addMethod === 'code' ? !friendCode : !friendEmail)}
                  data-testid="send-request-btn"
                >
                  {isAdding 
                    ? (isSpanish ? 'Enviando...' : 'Sending...') 
                    : (isSpanish ? 'Enviar Solicitud' : 'Send Request')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* My Friend Code Card */}
        <Card className="mb-4 bg-gradient-to-r from-[#00E5CC]/10 to-purple-500/10" data-testid="friend-code-card">
          <CardContent className="py-3 px-4">
            {myFriendCode ? (
              // Already has a friend code
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    {isSpanish ? 'Tu Código de Amigo' : 'Your Friend Code'}
                  </p>
                  <div className="flex items-center gap-1">
                    <span className="text-lg sm:text-xl font-mono font-bold tracking-wider text-[#00E5CC]">
                      {myFriendCode}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleCopyCode}
                      data-testid="copy-code-btn"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground max-w-[140px] text-right hidden sm:block">
                  {isSpanish 
                    ? 'Comparte este código'
                    : 'Share this code with friends'}
                </p>
              </div>
            ) : showSetCode ? (
              // Setting custom code form
              <div className="space-y-3">
                <p className="text-sm font-medium">
                  {isSpanish ? 'Elige tu código de amigo' : 'Choose your friend code'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSpanish 
                    ? '4-20 caracteres, alfanumérico. Una vez establecido, no se puede cambiar.'
                    : '4-20 characters, alphanumeric only. Once set, it cannot be changed.'}
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="MYCODE123"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    maxLength={20}
                    className="uppercase font-mono"
                    data-testid="custom-code-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleGenerateRandom}
                    title={isSpanish ? 'Generar aleatorio' : 'Generate random'}
                    data-testid="generate-random-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowSetCode(false)}>
                    {isSpanish ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleSetCode} 
                    disabled={isSettingCode || customCode.length < 4}
                    data-testid="set-code-btn"
                  >
                    {isSettingCode 
                      ? (isSpanish ? 'Guardando...' : 'Saving...') 
                      : (isSpanish ? 'Establecer Código' : 'Set Code')}
                  </Button>
                </div>
              </div>
            ) : (
              // No code yet - prompt to set one
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">
                    {isSpanish ? 'Tu Código de Amigo' : 'Your Friend Code'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isSpanish ? 'No has establecido un código aún' : 'You haven\'t set a code yet'}
                  </p>
                </div>
                <Button size="sm" onClick={() => { setShowSetCode(true); handleGenerateRandom(); }} data-testid="setup-code-btn">
                  {isSpanish ? 'Configurar' : 'Set Up'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs for Friends / Requests / Challenges */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-3">
            <TabsTrigger value="friends" className="flex items-center gap-1 text-xs sm:text-sm">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{isSpanish ? 'Amigos' : 'Friends'}</span>
              {friends.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{friends.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="challenges" className="flex items-center gap-1 text-xs sm:text-sm">
              <Swords className="h-4 w-4" />
              <span className="hidden sm:inline">{isSpanish ? 'Desafíos' : 'Challenges'}</span>
              {(pendingChallenges.length + activeChallenges.length) > 0 && (
                <Badge variant="default" className="ml-1 text-xs bg-[#00E5CC]">{pendingChallenges.length + activeChallenges.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-1 text-xs sm:text-sm">
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">{isSpanish ? 'Solicitudes' : 'Requests'}</span>
              {friendRequests.length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">{friendRequests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Challenges Tab */}
          <TabsContent value="challenges">
            <div className="space-y-4">
              {/* Pending Challenges */}
              {pendingChallenges.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-yellow-500" />
                      {isSpanish ? 'Desafíos Pendientes' : 'Pending Challenges'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pendingChallenges.map(challenge => (
                      <div key={challenge.challenge_id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <PixelAvatar avatarId={challenge.challenger_avatar} size={40} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{challenge.challenger_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {challenge.duration_days} {isSpanish ? 'días' : 'days'} • {challenge.challenge_type}
                          </p>
                          {challenge.message && (
                            <p className="text-xs italic mt-1">"{challenge.message}"</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAcceptChallenge(challenge.challenge_id)}>
                            {isSpanish ? 'Aceptar' : 'Accept'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeclineChallenge(challenge.challenge_id)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Active Challenges */}
              {activeChallenges.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Swords className="h-4 w-4 text-[#00E5CC]" />
                      {isSpanish ? 'Desafíos Activos' : 'Active Challenges'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {activeChallenges.map(challenge => {
                      const isChallenger = challenge.is_challenger;
                      const myStreak = isChallenger ? challenge.challenger_streak : challenge.challenged_streak;
                      const opponentStreak = isChallenger ? challenge.challenged_streak : challenge.challenger_streak;
                      const opponentName = isChallenger ? challenge.challenged_name : challenge.challenger_name;
                      const opponentAvatar = isChallenger ? challenge.challenged_avatar : challenge.challenger_avatar;
                      
                      return (
                        <div key={challenge.challenge_id} className="p-3 bg-gradient-to-r from-[#00E5CC]/10 to-purple-500/10 rounded-lg border border-[#00E5CC]/30">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <PixelAvatar avatarId={opponentAvatar} size={36} />
                              <div>
                                <p className="font-medium text-sm">vs {opponentName}</p>
                                <p className="text-xs text-muted-foreground">
                                  {challenge.duration_days} {isSpanish ? 'días' : 'days'} • {challenge.days_remaining || '?'} {isSpanish ? 'restantes' : 'left'}
                                </p>
                              </div>
                            </div>
                            <Trophy className="h-5 w-5 text-yellow-500" />
                          </div>
                          <div className="flex items-center justify-around mt-3 pt-2 border-t border-border">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-[#00E5CC]">{myStreak}</p>
                              <p className="text-xs text-muted-foreground">{isSpanish ? 'Tu racha' : 'Your streak'}</p>
                            </div>
                            <div className="text-xl font-bold text-muted-foreground">vs</div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-purple-500">{opponentStreak}</p>
                              <p className="text-xs text-muted-foreground">{isSpanish ? 'Su racha' : 'Their streak'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* No challenges */}
              {pendingChallenges.length === 0 && activeChallenges.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Swords className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">
                      {isSpanish ? 'Sin desafíos activos' : 'No active challenges'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {isSpanish 
                        ? 'Desafía a un amigo a una competencia de rachas'
                        : 'Challenge a friend to a streak competition'}
                    </p>
                    <Button onClick={() => setActiveTab('friends')}>
                      <Users className="h-4 w-4 mr-2" />
                      {isSpanish ? 'Ver Amigos' : 'View Friends'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Completed Challenges */}
              {completedChallenges.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-yellow-500" />
                      {isSpanish ? 'Historial' : 'History'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {completedChallenges.slice(0, 5).map(challenge => {
                      const isChallenger = challenge.is_challenger;
                      const won = (challenge.winner === 'challenger' && isChallenger) || 
                                  (challenge.winner === 'challenged' && !isChallenger);
                      const tied = challenge.winner === 'tie';
                      
                      return (
                        <div key={challenge.challenge_id} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                          <Badge variant={won ? 'default' : tied ? 'secondary' : 'outline'} className={won ? 'bg-green-500' : ''}>
                            {won ? (isSpanish ? 'Ganaste' : 'Won') : tied ? (isSpanish ? 'Empate' : 'Tie') : (isSpanish ? 'Perdiste' : 'Lost')}
                          </Badge>
                          <span className="text-sm">vs {isChallenger ? challenge.challenged_name : challenge.challenger_name}</span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Friends List */}
          <TabsContent value="friends">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : friends.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    {isSpanish ? 'No tienes amigos aún' : 'No friends yet'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {isSpanish 
                      ? 'Agrega amigos para competir en las clasificaciones'
                      : 'Add friends to compete on leaderboards'}
                  </p>
                  <Button onClick={() => setShowAddFriend(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {isSpanish ? 'Agregar Amigo' : 'Add Friend'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {friends.map((friendship) => (
                  <Card key={friendship.friendship_id} data-testid={`friend-${friendship.friend.user_id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        <PixelAvatar 
                          avatarId={friendship.friend.avatar || 'dino'} 
                          size={48} 
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{friendship.friend.display_name || friendship.friend.name}</p>
                          {friendship.friend.level ? (
                            <p className="text-sm text-muted-foreground truncate">
                              {isSpanish ? `Nivel ${friendship.friend.level}` : `Level ${friendship.friend.level}`}
                              {friendship.friend.total_xp != null && ` · ${friendship.friend.total_xp} XP`}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-1">
                            <div className="flex items-center gap-1 text-orange-500">
                              <Flame className="h-4 w-4" />
                              <span className="font-bold">{friendship.friend.current_streak || 0}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {isSpanish ? 'racha' : 'streak'}
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openChallengeModal({
                              friend_user_id: friendship.friend.user_id,
                              name: friendship.friend.name,
                              avatar: friendship.friend.avatar
                            })}
                            className="text-[#00E5CC] border-[#00E5CC] hover:bg-[#00E5CC]/10"
                            data-testid={`challenge-friend-${friendship.friend.user_id}`}
                          >
                            <Swords className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setRemoveFriend(friendship)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`remove-friend-${friendship.friend.user_id}`}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Friend Requests */}
          <TabsContent value="requests">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : friendRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    {isSpanish ? 'No hay solicitudes pendientes' : 'No pending requests'}
                  </h3>
                  <p className="text-muted-foreground">
                    {isSpanish 
                      ? 'Las solicitudes de amistad aparecerán aquí'
                      : 'Friend requests will appear here'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {friendRequests.map((request) => (
                  <Card key={request.friendship_id} data-testid={`request-${request.friendship_id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <PixelAvatar 
                          avatarId={request.from.avatar || 'dino'} 
                          colorId={request.from.avatar_color || 'aqua'}
                          size={48} 
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{request.from.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm"
                            onClick={() => handleAcceptRequest(request.friendship_id)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`accept-${request.friendship_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            {isSpanish ? 'Aceptar' : 'Accept'}
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeclineRequest(request.friendship_id)}
                            className="text-red-500 border-red-200 hover:bg-red-50"
                            data-testid={`decline-${request.friendship_id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Gift Trial Link */}
        <Card className="mt-6 border-purple-200" data-testid="gift-trial-card">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Gift className="h-8 w-8 text-purple-500" />
              <div className="flex-1">
                <p className="font-semibold">
                  {isSpanish ? '¿Quieres regalar una prueba VIP?' : 'Want to gift a VIP trial?'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isSpanish 
                    ? 'Regala 2 semanas gratis a un amigo'
                    : 'Give a friend 2 weeks free'}
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/subscription')}
                className="border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                {isSpanish ? 'Ir a Suscripción' : 'Go to Subscription'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Remove Friend Dialog */}
      <AlertDialog open={!!removeFriend} onOpenChange={(open) => !open && setRemoveFriend(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSpanish ? 'Eliminar Amigo' : 'Remove Friend'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isSpanish 
                ? `¿Estás seguro de que quieres eliminar a ${removeFriend?.friend?.name} de tu lista de amigos?`
                : `Are you sure you want to remove ${removeFriend?.friend?.name} from your friends?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isSpanish ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveFriend}
              className="bg-red-500 hover:bg-red-600"
            >
              {isSpanish ? 'Eliminar' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Challenge Friend Modal */}
      <Dialog open={showChallengeModal} onOpenChange={setShowChallengeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-[#00E5CC]" />
              {isSpanish ? 'Desafiar a un Amigo' : 'Challenge a Friend'}
            </DialogTitle>
            <DialogDescription>
              {selectedFriendForChallenge && (
                <span className="flex items-center gap-2 mt-2">
                  <PixelAvatar avatarId={selectedFriendForChallenge.avatar || 'dino'} size={32} />
                  <span className="font-medium text-foreground">{selectedFriendForChallenge.name}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Duration */}
            <div className="space-y-2">
              <Label>{isSpanish ? 'Duración del Desafío' : 'Challenge Duration'}</Label>
              <Select value={challengeDuration} onValueChange={setChallengeDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 {isSpanish ? 'días' : 'days'}</SelectItem>
                  <SelectItem value="14">14 {isSpanish ? 'días' : 'days'}</SelectItem>
                  <SelectItem value="30">30 {isSpanish ? 'días' : 'days'}</SelectItem>
                  <SelectItem value="60">60 {isSpanish ? 'días' : 'days'}</SelectItem>
                  <SelectItem value="90">90 {isSpanish ? 'días' : 'days'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Challenge Type */}
            <div className="space-y-2">
              <Label>{isSpanish ? 'Tipo de Racha' : 'Streak Type'}</Label>
              <Select value={challengeType} onValueChange={setChallengeType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overall">{isSpanish ? 'General' : 'Overall'}</SelectItem>
                  <SelectItem value="phrase">{isSpanish ? 'Frases vistas' : 'Phrases viewed'}</SelectItem>
                  <SelectItem value="todo">{isSpanish ? 'Lista de tareas' : 'To-do list'}</SelectItem>
                  <SelectItem value="timer">{isSpanish ? 'Temporizador' : 'Timer'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Optional Message */}
            <div className="space-y-2">
              <Label>{isSpanish ? 'Mensaje (opcional)' : 'Message (optional)'}</Label>
              <Input
                placeholder={isSpanish ? '¡Prepárate para pelear!' : "Get ready to rumble!"}
                value={challengeMessage}
                onChange={(e) => setChallengeMessage(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChallengeModal(false)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateChallenge} 
              disabled={isCreatingChallenge}
              className="bg-[#00E5CC] hover:bg-[#00E5CC]/90"
            >
              {isCreatingChallenge ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isSpanish ? 'Enviando...' : 'Sending...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Swords className="h-4 w-4" />
                  {isSpanish ? 'Enviar Desafío' : 'Send Challenge'}
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FriendsPage;
